export enum LogLevel {
  ERROR = 'ERROR',
  WARN = 'WARN',
  INFO = 'INFO',
  DEBUG = 'DEBUG'
}

export interface LogContext {
  method?: string;
  path?: string;
  url?: string;
  user?: string;
  userId?: string | number;
  component?: string;
  action?: string;
  correlationId?: string;
  userAgent?: string;
  statusCode?: number;
  duration?: number;
  [key: string]: any;
}

class Logger {
  private static correlationIdKey = 'X-Correlation-ID';
  private static currentCorrelationId: string | null = null;
  private static isDevelopment = process.env.NODE_ENV === 'development';
  private static isProduction = process.env.NODE_ENV === 'production';

  static generateCorrelationId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 11);
    return `xdm-${timestamp}-${random}`;
  }

  static getCorrelationId(): string {
    if (!this.currentCorrelationId) {
      this.currentCorrelationId = this.generateCorrelationId();
    }
    return this.currentCorrelationId;
  }

  static setCorrelationId(id: string): void {
    this.currentCorrelationId = id;
  }

  static clearCorrelationId(): void {
    this.currentCorrelationId = null;
  }

  static getCorrelationIdHeaderName(): string {
    return this.correlationIdKey;
  }

  private static formatTimestamp(): string {
    const now = new Date();
    return now.toISOString();
  }

  private static formatMessage(
    level: LogLevel,
    operation: string,
    context?: LogContext,
    message?: string,
    additionalFields?: Record<string, any>
  ): string {
    const timestamp = this.formatTimestamp();
    const parts: string[] = [`${timestamp} [${level}]`];

    parts.push(operation);

    const contextParts: string[] = [];
    if (context?.method) {
      contextParts.push(`Method: ${context.method}`);
    }
    if (context?.path) {
      contextParts.push(`Path: ${context.path}`);
    }
    if (context?.url) {
      contextParts.push(`URL: ${context.url}`);
    }
    if (context?.user || context?.userId) {
      const user = context.user || context.userId?.toString() || 'anonymous';
      contextParts.push(`User: ${user}`);
    }
    if (context?.correlationId) {
      contextParts.push(`CorrelationId: ${context.correlationId}`);
    }
    if (context?.statusCode) {
      contextParts.push(`StatusCode: ${context.statusCode}`);
    }
    if (context?.duration !== undefined) {
      contextParts.push(`Duration: ${context.duration}ms`);
    }

    if (context) {
      Object.keys(context).forEach(key => {
        if (!['method', 'path', 'url', 'user', 'userId', 'correlationId', 'statusCode', 'duration'].includes(key)) {
          const value = context[key];
          if (value !== undefined && value !== null) {
            contextParts.push(`${this.capitalizeFirst(key)}: ${this.formatValue(value)}`);
          }
        }
      });
    }

    if (additionalFields) {
      Object.keys(additionalFields).forEach(key => {
        const value = additionalFields[key];
        if (value !== undefined && value !== null) {
          contextParts.push(`${this.capitalizeFirst(key)}: ${this.formatValue(value)}`);
        }
      });
    }

    if (contextParts.length > 0) {
      parts.push(`- ${contextParts.join(' | ')}`);
    }

    if (message) {
      parts.push(`| Message: ${message}`);
    }

    return parts.join(' ');
  }

  private static formatValue(value: any): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (e) {
        return String(value);
      }
    }
    return String(value);
  }

  private static capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private static getCurrentUserInfo(): { user?: string; userId?: string | number } {
    try {
      const profileStr = localStorage.getItem('PROFILE');
      if (profileStr) {
        const profile = JSON.parse(profileStr);
        if (profile.username) {
          return { user: profile.username, userId: profile.id };
        }
      }

      return {};
    } catch (e) {
      return {};
    }
  }

  private static getUserAgent(): string {
    return navigator.userAgent || 'unknown';
  }

  private static buildContext(context?: LogContext): LogContext {
    const defaultContext: LogContext = {
      correlationId: this.getCorrelationId(),
      userAgent: this.getUserAgent(),
      path: window.location.pathname,
      url: window.location.href,
    };

    const userInfo = this.getCurrentUserInfo();
    if (userInfo.user || userInfo.userId) {
      defaultContext.user = userInfo.user;
      defaultContext.userId = userInfo.userId;
    }

    return { ...defaultContext, ...context };
  }

  private static sendToBugsnag(level: LogLevel, message: string, context?: LogContext, error?: Error) {
    try {
      if ((window as any).Bugsnag) {
        const severity = level === LogLevel.ERROR ? 'error' : 
                        level === LogLevel.WARN ? 'warning' : 
                        'info';
        
        if (error) {
          (window as any).Bugsnag.notify(error, (event: any) => {
            event.severity = severity;
            if (context) {
              event.addMetadata('context', context);
            }
            event.addMetadata('message', { message });
            if (context?.correlationId) {
              event.addMetadata('correlation', { correlationId: context.correlationId });
            }
          });
        } else {
          (window as any).Bugsnag.notify(new Error(message), (event: any) => {
            event.severity = severity;
            if (context) {
              event.addMetadata('context', context);
            }
            if (context?.correlationId) {
              event.addMetadata('correlation', { correlationId: context.correlationId });
            }
          });
        }
      }
    } catch (e) {
    }
  }

  static error(operation: string, message?: string, context?: LogContext, error?: Error, additionalFields?: Record<string, any>): void {
    const fullContext = this.buildContext(context);
    const logMessage = this.formatMessage(LogLevel.ERROR, operation, fullContext, message, additionalFields);
    
    console.error(logMessage, error || '');
    
    if (error || this.isProduction) {
      this.sendToBugsnag(LogLevel.ERROR, message || operation, fullContext, error);
    }
  }

  static warn(operation: string, message?: string, context?: LogContext, additionalFields?: Record<string, any>): void {
    const fullContext = this.buildContext(context);
    const logMessage = this.formatMessage(LogLevel.WARN, operation, fullContext, message, additionalFields);
    
    console.warn(logMessage);
    
    if (this.isProduction) {
      this.sendToBugsnag(LogLevel.WARN, message || operation, fullContext);
    }
  }

  static info(operation: string, message?: string, context?: LogContext, additionalFields?: Record<string, any>): void {
    const fullContext = this.buildContext(context);
    const logMessage = this.formatMessage(LogLevel.INFO, operation, fullContext, message, additionalFields);
    
    if (this.isDevelopment || this.isProduction) {
      console.info(logMessage);
    }
  }

  static debug(operation: string, message?: string, context?: LogContext, additionalFields?: Record<string, any>): void {
    if (this.isDevelopment) {
      const fullContext = this.buildContext(context);
      const logMessage = this.formatMessage(LogLevel.DEBUG, operation, fullContext, message, additionalFields);
      console.debug(logMessage);
    }
  }

  static logApiRequest(method: string, url: string, context?: LogContext): void {
    const requestContext: LogContext = {
      ...context,
      method: method.toUpperCase(),
      url,
    };
    this.debug('API Request', `${method.toUpperCase()} ${url}`, requestContext);
  }

  static logApiResponse(method: string, url: string, statusCode: number, duration: number, context?: LogContext): void {
    const requestContext: LogContext = {
      ...context,
      method: method.toUpperCase(),
      url,
      statusCode,
      duration,
    };

    if (statusCode >= 400) {
      this.error('API Error Response', `HTTP ${statusCode}`, requestContext);
    } else if (statusCode >= 300) {
      this.warn('API Redirect Response', `HTTP ${statusCode}`, requestContext);
    } else {
      this.info('API Success Response', `HTTP ${statusCode}`, requestContext);
    }
  }

  static logErrorResponse(operation: string, errorResponse: any, context?: LogContext): void {
    const additionalFields: Record<string, any> = {
      status: errorResponse.status,
      error: errorResponse.error,
      timestamp: errorResponse.timestamp,
    };

    if (errorResponse.validationErrors) {
      additionalFields.validationErrors = errorResponse.validationErrors;
    }

    if (errorResponse.details) {
      additionalFields.details = errorResponse.details;
    }

    const errorContext: LogContext = {
      ...context,
      statusCode: errorResponse.status,
      path: errorResponse.path || context?.path,
    };

    this.error(
      operation,
      errorResponse.message || errorResponse.error,
      errorContext,
      undefined,
      additionalFields
    );
  }
}

export default Logger;

